import { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'

import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

import { StudyDocQuote } from '../enrichStudyDocQuote'
import {
  PortraitMosaicKpiData,
  PortraitMosaicMode,
  PortraitMosaicQuote,
  PortraitMosaicStrip,
  PortraitMosaicTone,
} from '../PortraitMosaicStrip/PortraitMosaicStrip'
import { PortraitMosaicResponse } from '../PortraitMosaicStrip/buildPortraitMosaicTiles'
import { StudyDocPrintDigest } from '../StudyDocPrintDigest'

/** Host padding the band bleeds against. Peek/card `px-4` is the 1rem fallback. */
export const STUDY_DOC_CARD_PAD = {
  peek: '1rem',
  rail: '1.25rem',
  fullscreen: '1.5rem',
} as const

/**
 * How far a full-bleed band is allowed to escape.
 *
 * - `'card'` (default): bleed to the artifact card edge, cancelling the host's own
 *   `--study-doc-card-pad`. This is what every in-thread surface (peek, rail, expanded)
 *   wants; the addendum to docs/plans/portrait-mosaic-strip-ui.md is explicit that
 *   `w-screen`/100vw broke them.
 * - `'viewport'`: true edge-to-edge, for the fullscreen report surface only, where the
 *   band is meant to span the whole reading surface ("should be full width, like really
 *   full width", Fernando 2026-09-02). Uses the centered-escape geometry (100vw wide,
 *   pulled back by half the difference) so it works at any nesting depth.
 */
export type StudyDocFullBleedScope = 'card' | 'viewport'

/**
 * Geometry for the viewport escape, as the CSS custom properties the band's own style
 * reads. Kept as variables (rather than only a React prop) because the fullscreen host
 * renders StudyDocBody through intermediary components that do not forward band props —
 * setting these on the scroll container opts every band inside it into the wider escape.
 * `overflow-x: clip` on the same host absorbs the scrollbar-width difference between
 * `100vw` and the scroll container's client width instead of showing a horizontal
 * scrollbar (it does not turn the vertical `overflow-y: auto` into a scroll container of
 * its own the way `hidden` would).
 */
const VIEWPORT_BLEED_STYLE: CSSProperties = {
  ['--study-doc-band-width' as string]: '100vw',
  ['--study-doc-band-inline-margin' as string]: 'calc(50% - 50vw)',
  overflowX: 'clip',
}

export function studyDocCardPadStyle(
  pad: (typeof STUDY_DOC_CARD_PAD)[keyof typeof STUDY_DOC_CARD_PAD],
  fullBleedScope: StudyDocFullBleedScope = 'card',
): CSSProperties {
  const padStyle = { ['--study-doc-card-pad' as string]: pad }
  if (fullBleedScope === 'viewport') {
    return { ...padStyle, ...VIEWPORT_BLEED_STYLE }
  }
  return padStyle
}

/**
 * Tone → surface + ink tokens (D5). Dark is the default: `--platform-deep`
 * background, `--platform-deep-foreground` ink. Light swaps in the
 * `--document-page` family. Every value is a CSS custom property reference —
 * never a hardcoded hex/rgb — so a white-label org's token overrides still
 * apply here.
 *
 * Review #8: the wrapper also SCOPES `--foreground` / `--muted-foreground`
 * inline (the `BrandSetupStep.tsx:197-201` idiom) so `Heading`/`Text
 * color="muted"` inside keep reading the band's own tone instead of whatever
 * the surrounding page's light/dark mode happens to be. `--muted-foreground`
 * has no dedicated dark-on-`platform-deep` token, so it is derived with
 * `color-mix()` from the same two tokens already in play (60% ink over the
 * band background) rather than a new hardcoded gray — see
 * `StudyDocMagazineBand.test.tsx` for the contrast assertion backing the
 * 60% figure (~6.8:1 against `--platform-deep`, WCAG-AA requires 4.5:1).
 */
function toneStyle(tone: PortraitMosaicTone): CSSProperties {
  if (tone === 'auto') {
    // Default (2026-09-02 live QA: "this is light mode, so no green background"). The
    // `--card` family is the app's own theme-adaptive surface pair: light theme resolves
    // it to the near-white card surface with near-black ink, dark theme resolves it to
    // the same `#0A2120` deep teal as `--platform-deep` with white ink. So one token pair
    // gives a light slab in light mode and keeps the deep slab in dark mode, with no
    // JS theme detection and no hardcoded color. `--muted-foreground` is left to the
    // page's own theme value, which tracks light/dark the same way.
    return {
      backgroundColor: 'hsl(var(--card))',
      color: 'hsl(var(--card-foreground))',
      ['--foreground' as string]: 'var(--card-foreground)',
    }
  }
  if (tone === 'light') {
    return {
      backgroundColor: 'hsl(var(--document-page))',
      color: 'hsl(var(--document-page-foreground))',
      ['--foreground' as string]: 'var(--document-page-foreground)',
      ['--muted-foreground' as string]: 'var(--document-page-muted)',
    }
  }
  return {
    backgroundColor: 'hsl(var(--platform-deep))',
    color: 'hsl(var(--platform-deep-foreground))',
    ['--foreground' as string]: 'var(--platform-deep-foreground)',
    ['--muted-foreground' as string]:
      'color-mix(in srgb, hsl(var(--platform-deep-foreground)) 60%, hsl(var(--platform-deep)))',
  }
}

/**
 * Escape geometry for the band.
 *
 * `'card'` cancels the host's `--study-doc-card-pad` (peek 1rem / rail 1.25rem /
 * fullscreen 1.5rem; 1rem fallback matches peek `px-4`). Each value keeps a var indirection
 * so a host can widen the escape for every band inside it by setting
 * `--study-doc-band-width` / `--study-doc-band-inline-margin` (what
 * {@link studyDocCardPadStyle}'s `'viewport'` argument writes) without the prop having to
 * be threaded through components that do not forward it.
 *
 * `'viewport'` is the same centered escape written directly, for callers that DO hold the
 * prop. Print CSS zeros the negative margins either way; do not restore `w-screen`.
 */
function bleedStyle(scope: StudyDocFullBleedScope): CSSProperties {
  if (scope === 'viewport') {
    return {
      width: '100vw',
      maxWidth: '100vw',
      marginLeft: 'calc(50% - 50vw)',
      marginRight: 'calc(50% - 50vw)',
    }
  }
  return {
    width:
      'var(--study-doc-band-width, calc(100% + 2 * var(--study-doc-card-pad, 1rem)))',
    marginLeft:
      'var(--study-doc-band-inline-margin, calc(-1 * var(--study-doc-card-pad, 1rem)))',
    marginRight:
      'var(--study-doc-band-inline-margin, calc(-1 * var(--study-doc-card-pad, 1rem)))',
  }
}

interface StudyDocMagazineBandProps {
  title: string
  /**
   * @deprecated Accepted for source compatibility, never rendered.
   *
   * Live review 2026-09-03: "we should avoid having analysis there, analysis prose should
   * be in main prose area, this is literally a callout strip for quotes, so space only for
   * one header above or nothing really." The band is now kicker + short headline + wall +
   * quote + cite; the analysis sentence stays in the document's narrative bands, where it
   * already appears. `resolveMagazineBandSource` still composes a standfirst (its copy
   * rules — short option labels, no em/en dash — are still pinned by its own tests and are
   * the contract for any future consumer), this band simply does not render it.
   */
  standfirst?: string
  responses: Array<PortraitMosaicResponse>
  highlightedPersonaIds?: Array<string>
  populationId?: string
  pickerImages?: Record<string, string>
  /**
   * Curated quotes for the strip's featured question, prefix-matched by S7. This is what
   * `StudyDocPrintDigest` renders (unchanged) and what the live strip's rotation/advance
   * cycle through. Ring rendering / rotation is S2's job — this only carries the data down.
   */
  quotes?: Array<StudyDocQuote>
  /**
   * Every OTHER respondent's own answer to the featured question (`buildRowQuotes.ts`),
   * excluding anyone already in `quotes` — appended after the curated set for the LIVE
   * strip only (never reaches `StudyDocPrintDigest`), so every one of their tiles becomes
   * clickable without the idle rotation or the advance button ever landing on one. Omitted
   * or empty → the live strip renders exactly `quotes`, unchanged.
   */
  additionalQuotes?: Array<PortraitMosaicQuote>
  /**
   * How many LEADING entries of the live strip's quotes (`quotes` + `additionalQuotes`)
   * the idle rotation and the advance button cycle through. Omitted → rotate the whole
   * array (today's behavior). See `PortraitMosaicStrip`'s own doc comment for the full
   * contract.
   */
  rotationCount?: number
  /** Strip mode (S6 derives this from the featured result). Default 'quotes'. */
  mode?: PortraitMosaicMode
  /** KPI headline data (S6). Only rendered when `mode === 'kpi'`. */
  kpi?: PortraitMosaicKpiData
  /** Band tone (D5). Default 'auto': light surface in light mode, deep slab in dark mode. */
  tone?: PortraitMosaicTone
  /** How far the band bleeds. See {@link StudyDocFullBleedScope}. Default 'card'. */
  fullBleedScope?: StudyDocFullBleedScope
  className?: string
}

export function StudyDocMagazineBand(props: StudyDocMagazineBandProps) {
  const {
    title,
    responses,
    highlightedPersonaIds,
    populationId,
    pickerImages,
    quotes,
    additionalQuotes,
    rotationCount,
    mode,
    kpi,
    tone = 'auto',
    fullBleedScope = 'card',
    className,
  } = props

  // The live strip sees curated + row-sourced quotes; the print digest only ever sees the
  // curated set (`quotes`) — `additionalQuotes` entries are not `StudyDocQuote`-shaped
  // (no `name`/`initials`) and are not meant to appear in the bounded print digest.
  const liveQuotes: Array<PortraitMosaicQuote> | undefined =
    additionalQuotes && additionalQuotes.length > 0 ? [...(quotes ?? []), ...additionalQuotes] : quotes

  const { t } = useTranslation('app')

  const displayTitle = title.trim()
  // "In their words" only makes sense over a rotating quote strip — in kpi
  // mode this band leads with a KPI headline (see PortraitMosaicKpi), and the
  // label read wrong pinned above a stat (fable execution review, finding 4).
  // Default mirrors PortraitMosaicStrip's own mode default.
  const effectiveMode = mode ?? 'quotes'
  const showQuotesLabel = effectiveMode === 'quotes'

  if (!displayTitle && responses.length === 0) {
    return null
  }

  // D4 / S6 handoff: in kpi mode, `kpi.memberPersonaIds` is the real lit set
  // and takes precedence over the caller's `highlightedPersonaIds` (which is
  // `highlightMagazineStripPersonas`'s quotes-mode heuristic) — quotes mode
  // keeps using the caller's set unchanged.
  const effectiveHighlightedPersonaIds =
    mode === 'kpi' && kpi?.memberPersonaIds ? kpi.memberPersonaIds : highlightedPersonaIds

  return (
    <div
      className={cn(
        // D7: full-bleed of the artifact card, not the viewport. Width/margins
        // cancel `--study-doc-card-pad` (hosts: peek 1rem / rail 1.25rem /
        // fullscreen 1.5rem). Fallback 1rem matches peek `px-4`. Print CSS
        // zeros the negative margins. Do not restore `w-screen`.
        'relative max-w-none overflow-hidden',
        className,
      )}
      style={{
        ...toneStyle(tone),
        ...bleedStyle(fullBleedScope),
      }}
      data-testid="study-doc-magazine-band"
      data-tone={tone}
      data-full-bleed-scope={fullBleedScope}
      data-height-locked="true"
      data-quote-count={quotes?.length ?? 0}
    >
      {/*
        `pt-6` mirrors the existing `pb-6`: the inline surface (ThreadRunResults, D7
        `fullBleedScope="card"`) sat the "In their words" eyebrow flush against whatever
        content preceded the band — no breathing room at all (live QA, thread
        5bfa8a9d-ffd8-4ec2-abec-9285b9fd947a). Symmetric top/bottom padding matches the
        band's own spacing scale rather than inventing a new value.
      */}
      <div className="mx-auto max-w-[42rem] px-6 pb-6 pt-6">
        {showQuotesLabel ? (
          <Text
            as="p"
            size="sm"
            color="muted"
            className="font-sans font-normal not-italic normal-case tracking-normal"
            data-testid="study-doc-magazine-band-label"
          >
            {t('studyDocChrome.inTheirWords')}
          </Text>
        ) : null}
        {displayTitle ? (
          <Heading
            level={2}
            size="3xl"
            weight="normal"
            className="mt-2 font-sans font-normal not-italic normal-case tracking-normal"
            data-testid="study-doc-magazine-band-title"
          >
            {displayTitle}
          </Heading>
        ) : null}
      </div>
      <div className="print:hidden" data-print-hide="" data-testid="study-doc-magazine-band-live">
        <PortraitMosaicStrip
          responses={responses}
          highlightedPersonaIds={effectiveHighlightedPersonaIds}
          populationId={populationId}
          pickerImages={pickerImages}
          quotes={liveQuotes}
          rotationCount={rotationCount}
          mode={mode}
          kpi={kpi}
          tone={tone}
        />
      </div>
      <StudyDocPrintDigest
        mode={effectiveMode}
        quotes={quotes}
        responses={responses}
        pickerImages={pickerImages}
        highlightedPersonaIds={effectiveHighlightedPersonaIds}
        kpi={kpi}
      />
    </div>
  )
}
